import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import './SupplierManager.css';

const SupplierManager = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const [formData, setFormData] = useState({
    supplier_name: '',
    addr1: '',
    addr2: '',
    city: '',
    state: '',
    pincode: '',
    gst_num: '',
    pan_number: '',
    phone1: '',
    phone2: '',
    email: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});

  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9]{10}$/;

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/suppliers');
      setSuppliers(response.data);
    } catch (err) {
      setError('Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  };

  const validateField = (name, value) => {
    let error = '';
    if (!value && name !== 'addr2' && name !== 'phone2') {
      error = 'This field is required';
    } else {
      if (name === 'gst_num' && !gstRegex.test(value)) {
        error = 'Invalid GST format (e.g. 22AAAAA0000A1Z5)';
      } else if (name === 'pan_number' && !panRegex.test(value)) {
        error = 'Invalid PAN format (e.g. ABCDE1234F)';
      } else if (name === 'email' && !emailRegex.test(value)) {
        error = 'Invalid email format';
      } else if ((name === 'phone1' || (name === 'phone2' && value)) && !phoneRegex.test(value)) {
        error = 'Must be exactly 10 digits';
      } else if (name === 'pincode' && (!/^\d{6}$/.test(value))) {
        error = 'Must be exactly 6 digits';
      }
    }
    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validate on change
    const fieldError = validateField(name, value);
    setFieldErrors({ ...fieldErrors, [name]: fieldError });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation
    const newFieldErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newFieldErrors[key] = error;
    });

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError('Please fix the errors before submitting');
      return;
    }

    try {
      const payload = {
        ...formData,
        pincode: parseInt(formData.pincode, 10),
        phone2: formData.phone2 || null,
        addr2: formData.addr2 || null
      };

      if (editingSupplier) {
        await axios.put(`/api/suppliers/${editingSupplier.id}`, payload);
        setSuccess('Supplier updated successfully');
      } else {
        await axios.post('/api/suppliers', payload);
        setSuccess('Supplier created successfully');
      }
      resetForm();
      fetchSuppliers();
    } catch (err) {
      let errorMsg = 'Failed to save supplier';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map(e => `${e.loc[e.loc.length - 1]}: ${e.msg}`).join(', ');
        } else {
          errorMsg = err.response.data.detail;
        }
      }
      setError(errorMsg);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      supplier_name: supplier.supplier_name,
      addr1: supplier.addr1,
      addr2: supplier.addr2 || '',
      city: supplier.city,
      state: supplier.state,
      pincode: supplier.pincode.toString(),
      gst_num: supplier.gst_num,
      pan_number: supplier.pan_number,
      phone1: supplier.phone1,
      phone2: supplier.phone2 || '',
      email: supplier.email
    });
    setFieldErrors({});
    setShowForm(true);
  };

  const openConfirmDialog = (title, message, confirmText, onConfirm) => {
    setConfirmDialog({
      show: true,
      title,
      message,
      confirmText,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  const handleDelete = async (id) => {
    openConfirmDialog('Delete Supplier', 'Are you sure you want to delete this supplier?', 'Delete', async () => {
      try {
        await axios.delete(`/api/suppliers/${id}`);
        setSuccess('Supplier deleted successfully');
        fetchSuppliers();
      } catch (err) {
        setError('Failed to delete supplier');
      }
    });
  };

  const resetForm = () => {
    setFormData({
      supplier_name: '',
      addr1: '',
      addr2: '',
      city: '',
      state: '',
      pincode: '',
      gst_num: '',
      pan_number: '',
      phone1: '',
      phone2: '',
      email: ''
    });
    setEditingSupplier(null);
    setFieldErrors({});
    setShowForm(false);
  };

  return (
    <div className="supplier-manager">
      <div className="header-section">
        <h1>Supplier Maintenance</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          + Add Supplier
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h2>{editingSupplier ? 'Edit Supplier' : 'Add Supplier'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="supplier-form">
                <div className="form-group full-width">
                  <label>Supplier Name *</label>
                  <input
                    type="text"
                    name="supplier_name"
                    value={formData.supplier_name}
                    onChange={handleInputChange}
                    required
                  />
                  {fieldErrors.supplier_name && <span className="error-text">{fieldErrors.supplier_name}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Address Line 1 *</label>
                    <input
                      type="text"
                      name="addr1"
                      value={formData.addr1}
                      onChange={handleInputChange}
                      required
                    />
                    {fieldErrors.addr1 && <span className="error-text">{fieldErrors.addr1}</span>}
                  </div>
                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      name="addr2"
                      value={formData.addr2}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                    />
                    {fieldErrors.city && <span className="error-text">{fieldErrors.city}</span>}
                  </div>
                  <div className="form-group">
                    <label>State *</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                    />
                    {fieldErrors.state && <span className="error-text">{fieldErrors.state}</span>}
                  </div>
                  <div className="form-group">
                    <label>Pincode *</label>
                    <input
                      type="number"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      required
                    />
                    {fieldErrors.pincode && <span className="error-text">{fieldErrors.pincode}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>GST Number *</label>
                    <input
                      type="text"
                      name="gst_num"
                      value={formData.gst_num}
                      onChange={handleInputChange}
                      placeholder="22AAAAA0000A1Z5"
                      required
                    />
                    {fieldErrors.gst_num && <span className="error-text">{fieldErrors.gst_num}</span>}
                  </div>
                  <div className="form-group">
                    <label>PAN Number *</label>
                    <input
                      type="text"
                      name="pan_number"
                      value={formData.pan_number}
                      onChange={handleInputChange}
                      placeholder="ABCDE1234F"
                      required
                    />
                    {fieldErrors.pan_number && <span className="error-text">{fieldErrors.pan_number}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone 1 *</label>
                    <input
                      type="text"
                      name="phone1"
                      value={formData.phone1}
                      onChange={handleInputChange}
                      required
                    />
                    {fieldErrors.phone1 && <span className="error-text">{fieldErrors.phone1}</span>}
                  </div>
                  <div className="form-group">
                    <label>Phone 2</label>
                    <input
                      type="text"
                      name="phone2"
                      value={formData.phone2}
                      onChange={handleInputChange}
                    />
                    {fieldErrors.phone2 && <span className="error-text">{fieldErrors.phone2}</span>}
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                    {fieldErrors.email && <span className="error-text">{fieldErrors.email}</span>}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-success">
                    {editingSupplier ? 'Update' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        {loading ? (
          <p>Loading suppliers...</p>
        ) : (
          <table className="supplier-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>City</th>
                <th>GST Num</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.length > 0 ? (
                suppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td>{supplier.supplier_name}</td>
                    <td>{supplier.city}, {supplier.state}</td>
                    <td>{supplier.gst_num}</td>
                    <td>{supplier.phone1}</td>
                    <td>{supplier.email}</td>
                    <td>
                      <button className="btn btn-sm btn-info" onClick={() => handleEdit(supplier)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(supplier.id)}>Delete</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">No suppliers found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {confirmDialog.show && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>{confirmDialog.title || 'Confirm Action'}</h2>
              <button className="close-btn" onClick={closeConfirmDialog}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 0 }}>{confirmDialog.message}</p>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={closeConfirmDialog}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={executeConfirmDialogAction}>
                {confirmDialog.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierManager;
